import React, { useMemo, useState } from 'react';
import { Row, Col, DatePicker, Segmented, Select, Button, Table, Switch } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { EChartsOption } from 'echarts';
import PageContainer from '@/components/PageContainer';
import ChartCard from '@/components/ChartCard';
import Loading from '@/components/Loading';
import { useInterProvinceDayAhead } from '@/api/interProvince';
import { format2, sanitizeFilename } from '@/utils/charts';
import type { InterProvinceSummary } from '@/types/api';

const TRADE_SIDE_OPTIONS = [
  { label: '售方', value: '售方' },
  { label: '购方', value: '购方' },
];

const ENERGY_COLORS = [
  '#0d9488', '#52c41a', '#faad14', '#f5222d', '#13c2c2',
  '#722ed1', '#eb2f96', '#2f54eb', '#ff7a45', '#ffc53d',
  '#36cfc9', '#597ef7', '#9254de', '#f759ab', '#ff7875',
  '#ffa940', '#d3f261', '#87e8de', '#91d5ff', '#b37feb',
];

const InterProvincePage: React.FC = () => {
  const today = dayjs();
  const [date, setDate] = useState<Dayjs>(today);
  const [tradeSide, setTradeSide] = useState('售方');
  const [selectedProvinces, setSelectedProvinces] = useState<string[]>([]);
  const [showAll, setShowAll] = useState(false);

  const dateStr = date.format('YYYY-MM-DD');
  const { data: response, isLoading, error } = useInterProvinceDayAhead(dateStr, tradeSide);
  const rawData = response?.data;

  const allProvinces = rawData?.provinces ?? [];
  const summary = rawData?.summary ?? [];

  // Default to all provinces when data loads; respect user selection afterwards
  const effectiveSelectedProvinces = selectedProvinces.length > 0 ? selectedProvinces : allProvinces;

  // Determine which provinces to display
  const displayProvinces = useMemo(() => {
    if (!rawData) return [];
    // 按 summary 顺序（已按 total_volume 降序排列）过滤出用户选择的省份
    const ordered = summary.map((s) => s.province).filter((p) => effectiveSelectedProvinces.includes(p));
    // showAll=false 时只显示前10个
    return showAll ? ordered : ordered.slice(0, 10);
  }, [rawData, summary, effectiveSelectedProvinces, showAll]);

  // Debug logging
  if (rawData) {
    console.log('rawData provinces:', rawData.provinces.length);
    console.log('displayProvinces:', displayProvinces.length);
    console.log('summary:', summary.length);
  }

  const chartOption: EChartsOption = useMemo(() => {
    if (!rawData || displayProvinces.length === 0) {
      return {
        title: { text: '暂无数据', left: 'center', textStyle: { color: '#9ca3af' } },
        xAxis: { type: 'category', data: [] },
        yAxis: [{ type: 'value' }, { type: 'value' }],
        series: [],
      };
    }

    const timeLabels = rawData.time_labels;
    const priceSeries = displayProvinces.map((province, idx) => ({
      name: `${province}-电价`,
      type: 'line' as const,
      yAxisIndex: 0,
      data: rawData.price_curves[province] ?? [],
      smooth: true,
      symbol: 'none',
      lineStyle: { color: ENERGY_COLORS[idx % ENERGY_COLORS.length], width: 2 },
      itemStyle: { color: ENERGY_COLORS[idx % ENERGY_COLORS.length] },
    }));

    const volumeSeries = displayProvinces.map((province, idx) => ({
      name: `${province}-电量`,
      type: 'bar' as const,
      yAxisIndex: 1,
      data: rawData.volume_curves[province] ?? [],
      barMaxWidth: 6,
      itemStyle: { color: ENERGY_COLORS[idx % ENERGY_COLORS.length], opacity: 0.4 },
    }));

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: (params: any) => {
          if (!Array.isArray(params) || params.length === 0) return '';
          let html = `<div style="font-weight:600;margin-bottom:4px">${params[0].axisValue}</div>`;
          params.forEach((p: any) => {
            const unit = p.seriesName.endsWith('-电价') ? '元/MWh' : 'MW';
            html += `<div style="display:flex;align-items:center;gap:6px">
              <span style="width:8px;height:8px;border-radius:50%;background:${p.color}"></span>
              <span>${p.seriesName}: <strong>${format2(p.value)}</strong> ${unit}</span>
            </div>`;
          });
          return html;
        },
      },
      legend: {
        type: 'scroll',
        top: 4,
        left: 'center',
        right: 80,
        data: [...priceSeries.map((s) => s.name), ...volumeSeries.map((s) => s.name)],
      },
      toolbox: {
        show: false,
      },
      dataZoom: [
        { type: 'slider', bottom: 8, height: 18 },
        { type: 'inside' },
      ],
      grid: { left: 60, right: 60, top: 56, bottom: 56 },
      xAxis: {
        type: 'category',
        data: timeLabels,
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { color: '#9ca3af', fontSize: 10 },
      },
      yAxis: [
        {
          type: 'value',
          name: '电价 (元/MWh)',
          position: 'left',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#f3f4f6' } },
          axisLabel: { color: '#9ca3af', fontSize: 11 },
        },
        {
          type: 'value',
          name: '电量 (MW)',
          position: 'right',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { color: '#9ca3af', fontSize: 11 },
        },
      ],
      series: [...priceSeries, ...volumeSeries],
    };
  }, [rawData, displayProvinces]);

  // Table columns
  const columns = [
    { title: '省份', dataIndex: 'province', key: 'province', fixed: 'left' as const },
    { title: '均价 (元/MWh)', dataIndex: 'avg_price', key: 'avg_price', render: (v: number) => format2(v) },
    { title: '总电量 (MW)', dataIndex: 'total_volume', key: 'total_volume', render: (v: number) => format2(v), sorter: (a: InterProvinceSummary, b: InterProvinceSummary) => a.total_volume - b.total_volume },
    { title: '最高电价', key: 'max_price', render: (_: unknown, record: InterProvinceSummary) => `${format2(record.max_price)} (${record.max_price_time})` },
    { title: '最低电价', key: 'min_price', render: (_: unknown, record: InterProvinceSummary) => `${format2(record.min_price)} (${record.min_price_time})` },
  ];

  // Export CSV
  const handleExport = () => {
    if (!rawData || summary.length === 0) return;
    const headers = ['省份', '均价(元/MWh)', '总电量(MW)', '最高电价', '最高电价时段', '最低电价', '最低电价时段'];
    const rows = summary.map((s) => [
      s.province, s.avg_price, s.total_volume, s.max_price, s.max_price_time, s.min_price, s.min_price_time,
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = sanitizeFilename(`省间日前量价_${dateStr}_${tradeSide}.csv`);
    link.click();
  };

  return (
    <PageContainer title="省间日前量价">
      {/* Filter bar */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6} lg={4}>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>日期</div>
          <DatePicker
            value={date}
            onChange={(d) => d && setDate(d)}
            style={{ width: '100%' }}
            allowClear={false}
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>买/售方</div>
          <Segmented
            options={TRADE_SIDE_OPTIONS}
            value={tradeSide}
            onChange={(v) => setTradeSide(v as string)}
            block
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8}>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>省份筛选</div>
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="选择省份"
            value={selectedProvinces}
            onChange={setSelectedProvinces}
            options={allProvinces.map((p) => ({ label: p, value: p }))}
            style={{ width: '100%' }}
            maxTagCount="responsive"
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={4}>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>展示范围</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 32 }}>
            <Switch checked={showAll} onChange={setShowAll} />
            <span style={{ fontSize: 13 }}>{showAll ? '全部' : 'TOP 10'}</span>
          </div>
        </Col>
        <Col xs={24} sm={12} md={4} lg={4}>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>导出</div>
          <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!rawData}>
            导出 CSV
          </Button>
        </Col>
      </Row>

      {/* Chart */}
      <ChartCard
        title={`省间日前量价 — ${dateStr} — ${tradeSide}`}
        option={chartOption}
        loading={isLoading}
        error={error || undefined}
        height={480}
      />

      {/* Summary Table */}
      <div style={{ marginTop: 16, background: '#fff', borderRadius: 8, border: '2px solid #d9d9d9', padding: 24 }}>
        <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: 16 }}>省份汇总</div>
        {isLoading ? <Loading /> : (
          <Table
            dataSource={summary}
            columns={columns}
            rowKey="province"
            size="small"
            scroll={{ x: 800 }}
            pagination={false}
          />
        )}
      </div>
    </PageContainer>
  );
};

export default InterProvincePage;
